import { joinPath } from "./client";

const TOKEN_KEY = "userToken";
const LOGIN_KEY = "isLoggedIn";
const USER_KEY = "loginData";

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAdminSession(token, user) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(LOGIN_KEY, "True");
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAdminSession() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(LOGIN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getAdminUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || "null");
  } catch {
    return null;
  }
}

export function isAdminLoggedIn() {
  return localStorage.getItem(LOGIN_KEY) === "True" && !!getAdminToken();
}

const AUTH_UNPROTECTED = new Set([
  "v2/auth/login",
  "v2/auth/login/select",
  "v2/auth/signup",
  "v2/auth/send-otp",
  "v2/auth/verify-otp",
  "v2/auth/password-reset",
  "v2/auth/admin-set-password",
  "v2/auth/workspace-invites/preview",
  "v2/auth/workspace-invites/accept",
]);

let sessionRedirectStarted = false;

function redirectToLoginIfUnauthorized(path, status) {
  if (status !== 401) return;
  const normalized = String(path || "").replace(/^\//, "").split("?")[0];
  if (AUTH_UNPROTECTED.has(normalized)) return;
  if (sessionRedirectStarted) return;
  sessionRedirectStarted = true;
  clearAdminSession();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

/** Permission strings for the logged-in admin (from login/me response). */
export function getAdminPermissions() {
  return getAdminUser()?.permissions || [];
}

export function hasPermission(permission) {
  return getAdminPermissions().includes(permission);
}

export function hasAnyPermission(permissions) {
  const mine = getAdminPermissions();
  return (permissions || []).some((p) => mine.includes(p));
}

/** Refresh loginData from the server (role/permissions may change after deploy). */
export async function refreshAdminSession() {
  if (!getAdminToken()) return null;
  const res = await adminFetch("GET", "v2/auth/me");
  const user = res.data?.user;
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
  return user || null;
}

export function fetchWorkspace() {
  return adminFetch("GET", "v2/auth/workspace");
}

export function updateWorkspace(payload) {
  return adminFetch("PATCH", "v2/auth/workspace", payload);
}

export function generateWorkspaceCopy(payload = {}) {
  return adminFetch("POST", "v2/auth/workspace/generate-copy", payload);
}

export function suggestWorkspaceOnboarding(payload) {
  return adminFetch("POST", "v2/auth/workspace/onboarding/suggest", payload);
}

export function saveWorkspaceOnboarding(payload) {
  return adminFetch("PUT", "v2/auth/workspace/onboarding", payload);
}

export function fetchWorkspaceKnowledge() {
  return adminFetch("GET", "v2/auth/workspace/knowledge");
}

export function saveWorkspaceKnowledge(payload) {
  return adminFetch("PUT", "v2/auth/workspace/knowledge", payload);
}

export function fetchAdminWorkspaces() {
  return adminFetch("GET", "v2/auth/workspaces");
}

export function switchAdminWorkspace({ user_id, tenant_id } = {}) {
  return adminFetch("POST", "v2/auth/switch-workspace", {
    user_id: user_id || "",
    tenant_id: tenant_id || "",
  });
}

async function adminFetch(method, path, body, options = {}) {
  const token = getAdminToken();
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-access-token"] = token;
  let res;
  try {
    res = await fetch(joinPath(path), {
      method,
      headers,
      body: body != null ? JSON.stringify(body) : undefined,
      signal: options.signal,
    });
  } catch (networkErr) {
    const err = new Error(
      networkErr?.message === "Failed to fetch"
        ? "Could not reach the API. Confirm the backend is running (typically port 5001) and refresh."
        : networkErr?.message || "Network request failed"
    );
    err.code = "network_error";
    throw err;
  }
  let json;
  try {
    json = await res.json();
  } catch {
    const err = new Error(`Invalid response from server (${res.status})`);
    err.httpStatus = res.status;
    throw err;
  }
  if (!res.ok) {
    redirectToLoginIfUnauthorized(path, res.status);
    const err = new Error(json.message || `Request failed (${res.status})`);
    err.code = json.code;
    err.httpStatus = res.status;
    throw err;
  }
  return json;
}

export function adminLogin(email, password) {
  return adminFetch("POST", "v2/auth/login", {
    email,
    password,
    auth_type: "password",
  });
}

export function adminLoginSelect(email, password, tenantId, userId) {
  return adminFetch("POST", "v2/auth/login/select", {
    email,
    password,
    tenant_id: tenantId || "",
    user_id: userId || "",
  });
}

export function adminSignup(payload) {
  return adminFetch("POST", "v2/auth/signup", payload);
}

export function getTenantName() {
  return getAdminUser()?.tenant_name || "";
}

export function getTenantLogo() {
  return getAdminUser()?.tenant_logo || "";
}

export function authSendOtp(email) {
  return adminFetch("POST", "v2/auth/send-otp", { email });
}

export function authVerifyOtp(identifier, otp) {
  return adminFetch("POST", "v2/auth/verify-otp", { identifier, otp });
}

export function authPasswordReset(email, password) {
  return adminFetch("POST", "v2/auth/password-reset", { email, password });
}

export function adminSetPassword(email, password) {
  return adminFetch("POST", "v2/auth/admin-set-password", { email, password });
}

export function authCreateAdmin(payload) {
  return adminFetch("POST", "v2/auth/create-admin", payload);
}

export function inviteWorkspaceMember(payload) {
  return adminFetch("POST", "v2/auth/workspace-invites", {
    ...payload,
    frontend_origin: typeof window !== "undefined" ? window.location.origin : "",
  });
}

export function fetchWorkspaceInvites(status = "pending") {
  const q = status ? `?status=${encodeURIComponent(status)}` : "";
  return adminFetch("GET", `v2/auth/workspace-invites${q}`);
}

export function previewWorkspaceInvite(token) {
  return adminFetch("GET", `v2/auth/workspace-invites/preview?token=${encodeURIComponent(token)}`);
}

export function acceptWorkspaceInvite(payload) {
  return adminFetch("POST", "v2/auth/workspace-invites/accept", payload);
}

export function fetchAssessmentForm(options = {}, templateId = "") {
  const q = templateId ? `?template_id=${encodeURIComponent(templateId)}` : "";
  return adminFetch("GET", `v2/engagements/assessment-form${q}`, undefined, options);
}

export function updateAssessmentQuestion(payload) {
  return adminFetch("POST", "v2/engagements/assessment-form/questions", payload);
}

export function updateLiteQuestionSelection(payload) {
  return adminFetch("PUT", "v2/engagements/assessment-form/lite-selection", payload);
}

export function addAssessmentQuestion(payload) {
  return adminFetch("POST", "v2/engagements/assessment-form/questions/add", payload);
}

export function deleteAssessmentQuestion(surveyId, questionId) {
  return adminFetch("DELETE", `v2/engagements/assessment-form/questions/${encodeURIComponent(questionId)}`, { survey_id: surveyId });
}

export function reorderAssessmentQuestions(payload) {
  return adminFetch("PUT", "v2/engagements/assessment-form/questions/reorder", payload);
}

export function analyzeQuestionBulkUpload(payload, options = {}) {
  return adminFetch("POST", "v2/engagements/assessment-form/questions/bulk/analyze", payload, options);
}

export function applyQuestionBulkUpload(payload) {
  return adminFetch("POST", "v2/engagements/assessment-form/questions/bulk/apply", payload);
}

export function fetchSurveyTemplates(organizationId = "") {
  const q = organizationId
    ? `?organization_id=${encodeURIComponent(organizationId)}`
    : "";
  return adminFetch("GET", `v2/engagements/survey-templates${q}`);
}

export function createSurveyTemplate(payload) {
  return adminFetch("POST", "v2/engagements/survey-templates", payload);
}

export function fetchAssessmentFormats() {
  return adminFetch("GET", "v2/engagements/assessment-formats");
}

export function createAssessmentFormat(payload) {
  return adminFetch("POST", "v2/engagements/assessment-formats", payload);
}

export function updateAssessmentFormat(formatId, payload) {
  return adminFetch(
    "PUT",
    `v2/engagements/assessment-formats/${encodeURIComponent(formatId)}`,
    payload
  );
}

export function updateSurveyTemplate(templateId, payload) {
  return adminFetch("PUT", `v2/engagements/survey-templates/${encodeURIComponent(templateId)}`, payload);
}

export function activateSurveyTemplate(templateId) {
  return adminFetch(
    "POST",
    `v2/engagements/survey-templates/${encodeURIComponent(templateId)}/activate`
  );
}

export function archiveSurveyTemplate(templateId) {
  return adminFetch("DELETE", `v2/engagements/survey-templates/${encodeURIComponent(templateId)}`);
}

export function fetchInvites() {
  return adminFetch("GET", "v2/engagements/invites");
}

export function fetchCompanies() {
  return adminFetch("GET", "v2/engagements/companies");
}

export function saveCompany(data) {
  return adminFetch("POST", "v2/engagements/company", data);
}

export function fetchCompanyProfiles(options = {}) {
  return adminFetch("GET", "v2/engagements/companies/profiles", undefined, options);
}

export function createCompanyProfile(data) {
  return adminFetch("POST", "v2/engagements/company/create", data);
}

export function updateCompanyProfile(data) {
  return adminFetch("POST", "v2/engagements/company/update", data);
}

export function toggleCompanyPause(orgIds) {
  return adminFetch("POST", "v2/engagements/company/toggle-pause", { ids: orgIds });
}

export function handoverCompanyOwnership({ org_id, assigned_owner_id }) {
  return adminFetch("POST", "v2/engagements/company/handover", {
    org_id,
    assigned_owner_id,
  });
}

export function isSuperAdmin() {
  const user = getAdminUser();
  return user?.is_super_admin === true || user?.role_name === "Super Admin";
}

export function sendBatchOnboarding(payload) {
  return adminFetch("POST", "v2/engagements/invites/batch", payload);
}

export function sendSingleInvite(payload) {
  return adminFetch("POST", "v2/engagements/invites", payload);
}

export function disableInvite(surveyToken) {
  return adminFetch("POST", "v2/engagements/invites/disable", {
    survey_token: surveyToken,
  });
}

export function updateInvite(payload) {
  return adminFetch("POST", "v2/engagements/invites/update", payload);
}

export function resendInviteEmails(inviteIds, options = {}) {
  return adminFetch("POST", "v2/engagements/invites/send", {
    invite_ids: inviteIds,
    is_resend: options.isResend !== false,
    email_subject: options.subject,
    email_html: options.html,
  });
}

export function fetchEmailTemplates(type = "", { pack } = {}) {
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (pack) params.set("pack", pack);
  const q = params.toString() ? `?${params.toString()}` : "";
  return adminFetch("GET", `v2/engagements/email-templates${q}`);
}

export function fetchEmailTemplateById(id) {
  return adminFetch("GET", `v2/engagements/email-templates/${id}`);
}

/** Interpolation tokens, trigger anchors, and audiences — served by the backend
 *  so the editor can't drift from what the send path actually substitutes. */
export function fetchEmailTemplateConfig() {
  return adminFetch("GET", "v2/engagements/email-templates/config");
}

export function saveEmailTemplateRouting(routing) {
  return adminFetch("PUT", "v2/engagements/email-templates/routing", routing);
}

export function fetchEmailTemplateDraft(payload) {
  return adminFetch("POST", "v2/engagements/email-templates/draft", payload);
}

export function generateEmailTemplateDrafts(payload = {}) {
  return adminFetch("POST", "v2/engagements/email-templates/generate-drafts", payload);
}

export function fetchEmailAudiences() {
  return adminFetch("GET", "v2/engagements/email-audiences");
}

export function createEmailAudience(payload) {
  return adminFetch("POST", "v2/engagements/email-audiences", payload);
}

export function updateEmailAudience(id, payload) {
  return adminFetch("PUT", `v2/engagements/email-audiences/${id}`, payload);
}

export function createEmailTemplate(payload) {
  return adminFetch("POST", "v2/engagements/email-templates", payload);
}

export function updateEmailTemplate(id, payload) {
  return adminFetch("PUT", `v2/engagements/email-templates/${id}`, payload);
}

export function deleteEmailTemplate(id) {
  return adminFetch("DELETE", `v2/engagements/email-templates/${id}`);
}

export function previewEmailTemplate(payload) {
  return adminFetch("POST", "v2/engagements/email-templates/preview", payload);
}

export function sendTestEmail(payload) {
  return adminFetch("POST", "v2/engagements/email-templates/send-test", payload);
}

export function fetchReminderRules(organizationId = "") {
  const q = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : "";
  return adminFetch("GET", `v2/engagements/reminder-rules${q}`);
}

export function saveReminderSteps(payload) {
  return adminFetch("PUT", "v2/engagements/reminder-rules", payload);
}

export function fetchDispatchLog(params = {}) {
  const q = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""))
  ).toString();
  return adminFetch("GET", `v2/engagements/email-dispatch-log${q ? `?${q}` : ""}`);
}

export function planAdminAction({ message, draft = null, history = null, flowId = null, context = null }) {
  return adminFetch("POST", "v2/admin-actions/plan", {
    message,
    draft: draft || undefined,
    history: history?.length ? history : undefined,
    flow_id: flowId || undefined,
    context: context && typeof context === "object" ? context : undefined,
  });
}

export function executeAdminAction(payload) {
  return adminFetch("POST", "v2/admin-actions/execute", payload);
}

export function cancelAdminActionFlow({ flowId, reason = "user_cancelled", atStep = null }) {
  return adminFetch("POST", "v2/admin-actions/flow/cancel", {
    flow_id: flowId,
    reason,
    at_step: atStep || undefined,
  });
}

// ---------------------------------------------------------------------------
// Role-based access control — role CRUD + admin user role/company assignment
// ---------------------------------------------------------------------------

export function fetchPermissionRegistry() {
  return adminFetch("GET", "v2/admin/roles/permissions");
}

export function fetchRoles() {
  return adminFetch("GET", "v2/admin/roles");
}

export function createRole(payload) {
  return adminFetch("POST", "v2/admin/roles", payload);
}

export function updateRole(roleId, payload) {
  return adminFetch("PUT", `v2/admin/roles/${roleId}`, payload);
}

export function deleteRole(roleId) {
  return adminFetch("DELETE", `v2/admin/roles/${roleId}`);
}

export function fetchAdminUsers({ status } = {}) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : "";
  return adminFetch("GET", `v2/admin/roles/users${qs}`);
}

export function assignAdminRole(userId, { roleId, companyIds }) {
  return adminFetch("PUT", `v2/admin/roles/users/${userId}`, {
    role_id: roleId,
    company_ids: companyIds || [],
  });
}

export function deactivateAdminUser(userId) {
  return adminFetch("POST", `v2/admin/roles/users/${userId}/deactivate`, {});
}

export function reactivateAdminUser(userId) {
  return adminFetch("POST", `v2/admin/roles/users/${userId}/reactivate`, {});
}

const V1_API_BASE = (process.env.REACT_APP_V1_API_URL || "").replace(/\/?$/, "");

function reportPath(pathAndQuery) {
  const q = String(pathAndQuery).replace(/^\/+/, "");
  if (q.startsWith("v2/reports/")) return q;
  if (q.startsWith("report/")) return `v2/reports/${q.slice("report/".length)}`;
  return `v2/reports/${q}`;
}

export function reportPdfUrl(pathAndQuery) {
  return joinPath(reportPath(pathAndQuery));
}

/** Open team or company PDF in a new tab (v2 API, same behavior as v1). */
export function openReportPdf(pathAndQuery) {
  window.open(reportPdfUrl(pathAndQuery), "_blank", "noopener,noreferrer");
}

export function openTeamReportPdf(companyName) {
  openReportPdf(
    `generate_reportpdf?company_name=${encodeURIComponent(companyName)}`
  );
}

export function openCompanyReportPdf(companyName) {
  openReportPdf(
    `Company_reportpdf?company_name=${encodeURIComponent(companyName)}`
  );
}

/** Download Excel export (individual / company / group). */
export async function downloadReportExport(pathAndQuery) {
  const token = getAdminToken();
  const res = await fetch(joinPath(reportPath(pathAndQuery)), {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "x-access-token": token } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Download failed (${res.status})`);
  }
  const contentType = res.headers.get("Content-Type") || "";
  if (contentType.includes("application/json")) {
    let err;
    try {
      err = await res.json();
    } catch {
      err = {};
    }
    throw new Error(err.message || "Unexpected JSON from report API");
  }
  const blob = await res.blob();
  let filename = "Report.xlsx";
  const cd = res.headers.get("Content-Disposition");
  if (cd) {
    const m = cd.match(/filename="(.+)"/);
    if (m?.[1]) filename = m[1].replace(/^"|"$/g, "");
  }
  const fileUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = fileUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(fileUrl);
}

/** @deprecated use downloadReportExport — kept for optional V1 fallback */
export async function downloadV1Report(pathAndQuery) {
  if (V1_API_BASE) {
    const token = getAdminToken();
    const url = `${V1_API_BASE}/${String(pathAndQuery).replace(/^\/+/, "")}`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { "x-access-token": token } : {}),
      },
    });
    if (res.ok) {
      const contentType = res.headers.get("Content-Type") || "";
      if (!contentType.includes("application/json")) {
        const blob = await res.blob();
        const fileUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = "Report.xlsx";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(fileUrl);
        return;
      }
    }
  }
  return downloadReportExport(pathAndQuery);
}
